import React, { type MouseEventHandler, type KeyboardEventHandler, type KeyboardEvent, useContext } from 'react';

import { useSmartlingStrings } from '@lucid/hooks';

import LayoutContext from '../../context/layout-context';
import Box from '../../box/box';
import Icon from '../../icon/icon';

interface QuoteCarouselArrowType {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Direction the arrow is facing */
  direction?: string;
  /** Event that will occur onClick */
  onClick?: MouseEventHandler;
  /** Event that will occur onKeyUp */
  onKeyUp?: KeyboardEventHandler;
}

const QuoteCarouselArrow = ({ className, direction = 'right', onClick, onKeyUp, ...rest }: QuoteCarouselArrowType) => {
  const { shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_quote_carousel_arrow_right', 'mktg_quote_carousel_arrow_left'],
    shortCode,
  });

  const handleOnKeyUp = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && onKeyUp) {
      onKeyUp(event);
    }
  };

  return (
    <Box
      {...rest}
      css={theme => ({
        maxWidth: theme.space.s120,
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        '&:hover': {
          '& path:not([fill="none"])': {
            fill: theme.colors.coolGrey[60],
          },
        },
      })}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
      onClick={onClick}
      role="button"
    >
      <Box
        aria-label={withSmartling(`mktg_quote_carousel_arrow_${direction}`)}
        css={theme => ({
          height: theme.space.s40,
          width: theme.space.s40,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        })}
        onKeyUp={handleOnKeyUp}
        role="button"
        tabIndex={0}
      >
        <Icon
          css={{
            transform: direction === 'right' ? '' : 'rotate(180deg)',
            height: '24px',
          }}
          type="CarotRight"
        />
      </Box>
    </Box>
  );
};

export default QuoteCarouselArrow;
